import { useEffect, useState } from "react";
import axios from "axios";
import { API_BASE, formatApiError } from "../components/AuthContext";
import { Clock, Users, Briefcase, RefreshCw, Loader2 } from "lucide-react";

function hrs(m) { return (Number(m || 0) / 60).toFixed(1); }
function todayISO(off = 0) {
  const d = new Date(); d.setDate(d.getDate() + off);
  return d.toISOString().slice(0, 10);
}

export default function TeamTime() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ start: todayISO(-30), end: todayISO(), user_id: "" });

  const reload = async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filter.start) params.append("start", filter.start);
    if (filter.end) params.append("end", filter.end);
    if (filter.user_id) params.append("user_id", filter.user_id);
    try {
      const r = await axios.get(`${API_BASE}/team/time?${params}`);
      setData(r.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { reload(); }, [filter.start, filter.end, filter.user_id]);  // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="px-10 py-12 max-w-[1240px]" data-testid="team-time-page">
      <div className="overline mb-2">Manager view</div>
      <h1 className="font-heading text-4xl mb-2" style={{ fontWeight: 500 }}>
        Team <em className="italic" style={{ color: "var(--accent)" }}>time</em>
      </h1>
      <p className="text-[var(--ink-2)] mb-8">Org-wide rollup of every time entry across users and jobs.</p>

      <div className="card p-4 mb-6 flex flex-wrap items-center gap-3" data-testid="team-time-filters">
        <label className="overline text-[10px]">Range</label>
        <input type="date" value={filter.start}
          onChange={(e) => setFilter({ ...filter, start: e.target.value })}
          data-testid="team-time-start"
          className="bg-white border border-[var(--border)] rounded-md px-3 py-1.5 text-[13px]" />
        <span className="text-[var(--ink-3)] text-[12px]">to</span>
        <input type="date" value={filter.end}
          onChange={(e) => setFilter({ ...filter, end: e.target.value })}
          data-testid="team-time-end"
          className="bg-white border border-[var(--border)] rounded-md px-3 py-1.5 text-[13px]" />
        <select
          value={filter.user_id}
          onChange={(e) => setFilter({ ...filter, user_id: e.target.value })}
          data-testid="team-time-user-filter"
          className="bg-white border border-[var(--border)] rounded-md px-3 py-1.5 text-[13px]"
        >
          <option value="">All users</option>
          {data && Object.entries(data.totals.by_user || {}).map(([uid, u]) => (
            <option key={uid} value={uid}>{u.name}</option>
          ))}
        </select>
        <button onClick={reload} className="btn-secondary ml-auto" data-testid="team-time-refresh">
          {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Refresh
        </button>
      </div>

      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8" data-testid="team-time-stats">
          <Stat icon={<Clock size={14} />} label="Total hours" value={hrs(data.totals.total_minutes)} />
          <Stat icon={<Clock size={14} />} label="Billable hours" value={hrs(data.totals.billable_minutes)} accent="var(--moss)" />
          <Stat icon={<Users size={14} />} label="People" value={Object.keys(data.totals.by_user).length} />
          <Stat icon={<Briefcase size={14} />} label="Jobs" value={Object.keys(data.totals.by_job).length} />
        </div>
      )}

      {!loading && data && (
        <>
          <h2 className="overline mb-3">By person</h2>
          <div className="card overflow-hidden mb-8" data-testid="team-time-by-user">
            <table className="report-table">
              <thead><tr><th>Person</th><th>Hours</th><th>Billable hrs</th><th>Utilization</th></tr></thead>
              <tbody>
                {Object.entries(data.totals.by_user).map(([uid, u]) => (
                  <tr key={uid} data-testid={`team-time-user-${uid}`}>
                    <td className="font-medium">{u.name}</td>
                    <td className="font-mono">{hrs(u.minutes)}</td>
                    <td className="font-mono">{hrs(u.billable)}</td>
                    <td>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-1.5 bg-[var(--subtle)] rounded-full overflow-hidden max-w-[160px]">
                          <div className="h-full" style={{
                            width: `${u.minutes ? (u.billable / u.minutes) * 100 : 0}%`,
                            background: "var(--moss)",
                          }} />
                        </div>
                        <span className="text-[11px] font-mono">{u.minutes ? Math.round((u.billable / u.minutes) * 100) : 0}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
                {Object.keys(data.totals.by_user).length === 0 && (
                  <tr><td colSpan="4" className="text-center text-[var(--ink-3)] py-8">No entries in this range.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <h2 className="overline mb-3">By job</h2>
          <div className="card overflow-hidden" data-testid="team-time-by-job">
            <table className="report-table">
              <thead><tr><th>Job</th><th>Hours</th><th>Billable hrs</th></tr></thead>
              <tbody>
                {Object.entries(data.totals.by_job).map(([jid, j]) => (
                  <tr key={jid} data-testid={`team-time-job-${jid}`}>
                    <td className="font-medium">{j.title}</td>
                    <td className="font-mono">{hrs(j.minutes)}</td>
                    <td className="font-mono">{hrs(j.billable)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function Stat({ icon, label, value, accent }) {
  return (
    <div className="card p-4" data-testid={`team-time-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="overline mb-1 inline-flex items-center gap-1.5">{icon} {label}</div>
      <div className="font-heading text-3xl tabular" style={{ fontWeight: 500, color: accent || "var(--ink)" }}>{value}</div>
    </div>
  );
}
